import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PageHeader, PrimaryActionButton } from '../../components/common/PageHeader';
import {
  PlacesSearchField,
  type SelectedPlace,
} from '../../components/maps/PlacesSearchField';
import { visitService } from '../../services/visit.service';
import { employeeService } from '../../services/employee.service';
import { customerService } from '../../services/customer.service';
import type { VisitAssignment } from '../../types';
import { Priority, VisitStatus } from '../../types';

type LocationMode = 'customer' | 'adhoc';

/** Presets for area coverage (not a single shop pin). */
const AREA_RADIUS_PRESETS = [
  { value: 200, label: '200m', hint: 'Small spot' },
  { value: 500, label: '500m', hint: 'Market / block' },
  { value: 800, label: '800m', hint: 'Locality (Karol Bagh)' },
  { value: 1500, label: '1.5km', hint: 'Large area' },
  { value: 2500, label: '2.5km', hint: 'Wide zone' },
] as const;

const DEFAULT_AREA_RADIUS = 800;

interface AssignFormValues {
  employeeId: string;
  customerId: string;
  assignedDate: string;
  scheduledTime: string;
  priority: Priority;
  notes: string;
  areaLabel: string;
}

function formatRadius(meters: number): string {
  if (meters >= 1000) return `${(meters / 1000).toFixed(meters % 1000 === 0 ? 0 : 1)} km`;
  return `${meters} m`;
}

export function VisitAssignmentsPage() {
  const [open, setOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [filterDate, setFilterDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [locationMode, setLocationMode] = useState<LocationMode>('customer');
  const [placeQuery, setPlaceQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(null);
  const [areaRadius, setAreaRadius] = useState(DEFAULT_AREA_RADIUS);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['visits', page, rowsPerPage, filterDate],
    queryFn: () => visitService.getAll({ page: page + 1, limit: rowsPerPage, date: filterDate }),
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeeService.getAll({ page: 1, limit: 100, status: 'ACTIVE' as never }),
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => customerService.getAll({ page: 1, limit: 200 }),
  });

  const { register, handleSubmit, reset, watch, setValue } = useForm<AssignFormValues>({
    defaultValues: {
      employeeId: '',
      customerId: '',
      assignedDate: filterDate,
      scheduledTime: '',
      priority: Priority.MEDIUM,
      notes: '',
      areaLabel: '',
    },
  });

  const areaLabel = watch('areaLabel');

  const resetDialog = useCallback(() => {
    setOpen(false);
    setLocationMode('customer');
    setPlaceQuery('');
    setSelectedPlace(null);
    setAreaRadius(DEFAULT_AREA_RADIUS);
    reset({
      employeeId: '',
      customerId: '',
      assignedDate: filterDate,
      scheduledTime: '',
      priority: Priority.MEDIUM,
      notes: '',
      areaLabel: '',
    });
  }, [filterDate, reset]);

  const createMutation = useMutation({
    mutationFn: async (values: AssignFormValues) => {
      let customerId = values.customerId;

      if (locationMode === 'adhoc') {
        if (!selectedPlace) {
          throw new Error('Select an area/address from Google suggestions');
        }

        const label = values.areaLabel.trim() || selectedPlace.name;
        const customer = await customerService.create({
          name: label.startsWith('Area:') ? label : `Area: ${label}`,
          address: selectedPlace.address,
          latitude: selectedPlace.latitude,
          longitude: selectedPlace.longitude,
          // Cover the locality, not just a single pin
          geofenceRadius: areaRadius,
          contactPerson: 'Ad-hoc area visit',
        });
        customerId = customer.id;
      }

      if (!customerId) {
        throw new Error('Select a customer or an ad-hoc area');
      }

      return visitService.create({
        employeeId: values.employeeId,
        customerId,
        assignedDate: values.assignedDate,
        scheduledTime: values.scheduledTime || undefined,
        priority: values.priority,
        notes:
          values.notes ||
          (locationMode === 'adhoc' && selectedPlace
            ? `Ad-hoc area visit (${formatRadius(areaRadius)} radius): ${selectedPlace.address}`
            : undefined),
      });
    },
    onSuccess: () => {
      toast.success(locationMode === 'adhoc' ? 'Area visit assigned' : 'Visit assigned');
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      resetDialog();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(e.response?.data?.message || e.message || 'Failed to assign visit');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => visitService.updateStatus(id, VisitStatus.CANCELLED),
    onSuccess: () => {
      toast.success('Visit cancelled');
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      setCancelId(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(e.response?.data?.message || e.message || 'Failed to cancel visit');
    },
  });

  const columns = [
    {
      id: 'employee',
      label: 'Employee',
      minWidth: 140,
      render: (row: VisitAssignment) => row.employee?.name ?? '—',
    },
    {
      id: 'customer',
      label: 'Location / Customer',
      minWidth: 180,
      render: (row: VisitAssignment) => {
        const name = row.customer?.name ?? '—';
        const isAdhoc = name.startsWith('Area:');
        return (
          <Box>
            <Typography variant="body2" fontWeight={isAdhoc ? 600 : 400}>
              {name}
            </Typography>
            {isAdhoc && (
              <Chip label="Ad-hoc area" size="small" color="info" variant="outlined" sx={{ mt: 0.5 }} />
            )}
          </Box>
        );
      },
    },
    {
      id: 'date',
      label: 'Date',
      render: (row: VisitAssignment) => format(new Date(row.assignedDate), 'dd MMM yyyy'),
    },
    {
      id: 'time',
      label: 'Scheduled',
      render: (row: VisitAssignment) => row.scheduledTime ?? 'Anytime',
    },
    {
      id: 'priority',
      label: 'Priority',
      render: (row: VisitAssignment) => (
        <Chip
          label={row.priority}
          size="small"
          color={row.priority === 'HIGH' ? 'error' : row.priority === 'MEDIUM' ? 'warning' : 'default'}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: VisitAssignment) => <StatusChip status={row.status} />,
    },
    {
      id: 'actions',
      label: '',
      render: (row: VisitAssignment) =>
        row.status === VisitStatus.PENDING ? (
          <Tooltip title="Cancel">
            <IconButton size="small" color="error" onClick={() => setCancelId(row.id)}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box>
      <PageHeader
        compact
        title="Visit Assignments"
        subtitle="Assign customer visits or ad-hoc areas to employees"
        action={
          <PrimaryActionButton
            label="Assign Visit"
            icon={<AddIcon />}
            onClick={() => {
              setValue('assignedDate', filterDate);
              setOpen(true);
            }}
          />
        }
      />

      <Box mb={2} maxWidth={220}>
        <TextField
          label="Filter by Date"
          type="date"
          size="small"
          fullWidth
          value={filterDate}
          onChange={(e) => {
            setFilterDate(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.total ?? 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No visits for this date"
      />

      <Dialog open={open} onClose={resetDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <DialogTitle>Assign Visit</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                select
                label="Employee"
                fullWidth
                required
                defaultValue=""
                {...register('employeeId', { required: true })}
              >
                <MenuItem value="">Select employee</MenuItem>
                {(employeesData?.data ?? []).map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Where should they go?
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={locationMode}
                  onChange={(_, next: LocationMode | null) => {
                    if (!next) return;
                    setLocationMode(next);
                    setSelectedPlace(null);
                    setPlaceQuery('');
                    setAreaRadius(DEFAULT_AREA_RADIUS);
                    setValue('customerId', '');
                    setValue('areaLabel', '');
                  }}
                >
                  <ToggleButton value="customer">Existing customer</ToggleButton>
                  <ToggleButton value="adhoc">Ad-hoc area / place</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {locationMode === 'customer' ? (
                <TextField
                  select
                  label="Customer"
                  fullWidth
                  required
                  defaultValue=""
                  {...register('customerId', {
                    required: locationMode === 'customer',
                  })}
                >
                  <MenuItem value="">Select customer</MenuItem>
                  {(customersData?.data ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Alert severity="info">
                    For localities like <strong>Karol Bagh</strong>, search the area and set a{' '}
                    <strong>coverage radius</strong>. Employee is marked arrived anywhere inside
                    that circle — not only at one exact pin.
                  </Alert>
                  <PlacesSearchField
                    value={placeQuery}
                    onChange={setPlaceQuery}
                    onPlaceSelected={(place) => {
                      setSelectedPlace(place);
                      setPlaceQuery(place.address);
                      if (!areaLabel.trim()) {
                        setValue('areaLabel', place.name);
                      }
                    }}
                    label="Search area / place"
                    placeholder="e.g. Karol Bagh, New Delhi"
                  />
                  <TextField
                    label="Location label (optional)"
                    fullWidth
                    placeholder="e.g. Karol Bagh market visit"
                    helperText="Shown on the visit list. Defaults to the place name."
                    {...register('areaLabel')}
                  />

                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        Area coverage radius
                      </Typography>
                      <Chip size="small" color="primary" label={formatRadius(areaRadius)} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Larger radius = whole locality. Smaller = near one point only.
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                      {AREA_RADIUS_PRESETS.map((preset) => (
                        <Chip
                          key={preset.value}
                          label={`${preset.label}`}
                          size="small"
                          clickable
                          color={areaRadius === preset.value ? 'primary' : 'default'}
                          variant={areaRadius === preset.value ? 'filled' : 'outlined'}
                          onClick={() => setAreaRadius(preset.value)}
                          title={preset.hint}
                        />
                      ))}
                    </Box>
                    <Slider
                      value={areaRadius}
                      min={100}
                      max={3000}
                      step={50}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => formatRadius(v)}
                      marks={[
                        { value: 200, label: '200m' },
                        { value: 800, label: '800m' },
                        { value: 1500, label: '1.5km' },
                        { value: 3000, label: '3km' },
                      ]}
                      onChange={(_, v) => setAreaRadius(v as number)}
                    />
                  </Box>

                  {selectedPlace && (
                    <Alert severity="success">
                      <strong>{selectedPlace.name}</strong>
                      <br />
                      {selectedPlace.address}
                      <br />
                      Center pin + <strong>{formatRadius(areaRadius)}</strong> coverage circle
                      (lat {selectedPlace.latitude.toFixed(5)}, lng {selectedPlace.longitude.toFixed(5)})
                    </Alert>
                  )}
                </Box>
              )}

              <TextField
                label="Visit Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                {...register('assignedDate', { required: true })}
              />
              <TextField
                label="Scheduled Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...register('scheduledTime')}
              />
              <TextField
                select
                label="Priority"
                fullWidth
                defaultValue={Priority.MEDIUM}
                {...register('priority')}
              >
                <MenuItem value={Priority.LOW}>Low</MenuItem>
                <MenuItem value={Priority.MEDIUM}>Medium</MenuItem>
                <MenuItem value={Priority.HIGH}>High</MenuItem>
              </TextField>
              <TextField label="Notes" multiline rows={2} fullWidth {...register('notes')} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!cancelId}
        title="Cancel Visit"
        message="Cancel this visit assignment?"
        onConfirm={() => cancelId && cancelMutation.mutate(cancelId)}
        onCancel={() => setCancelId(null)}
        confirmLabel="Cancel Visit"
        confirmColor="error"
      />
    </Box>
  );
}
